<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-wrap">
      <div class="w-1/2 px-3 mb-3">
        <input-select-validated
          ref="company.billing.paymentMethod"
          v-model="company.billing.paymentMethod"
          input-class="w-full"
          :label="$t('modals.company.billing.paymentMethod')"
          :options="paymentMethods"
          rules="required"
        />
      </div>
      <div class="w-1/2 px-3 mb-3">
        <input-text-validated
          ref="company.billing.vatId"
          v-model="company.billing.vatId"
          input-class="w-full"
          :label="$t('modals.company.billing.vatId')"
          :disabled="company.billing.hasNoVatId"
          :rules="vatIdRules"
        />
        <button
          type="button"
          class="mt-1 text-sm text-blue-600 underline"
          @click="toggleNoVatId"
        >
          {{ company.billing.hasNoVatId ? $t('modals.company.billing.hasVatId') : $t('modals.company.billing.noVatId') }}
        </button>
      </div>

      <div
        v-if="company.billing.hasNoVatId"
        class="w-1/2 px-3 mb-3"
      >
        <input-text-validated
          ref="company.billing.taxNumber"
          v-model="company.billing.taxNumber"
          input-class="w-full"
          :label="$t('modals.company.billing.taxNumber')"
          rules="maxLength:50"
        />
      </div>

      <div class="w-1/2 px-3 mb-3">
        <input-text-validated
          ref="company.billing.bic"
          v-model="company.billing.bic"
          input-class="w-full"
          label="BIC"
          rules="bic"
        />
      </div>
      <div class="w-1/2 px-3 mb-3">
        <input-text-validated
          ref="company.billing.iban"
          v-model="company.billing.iban"
          input-class="w-full"
          label="IBAN"
          rules="iban"
        />
      </div>
      <div class="w-1/2 px-3 mb-3">
        <input-date-validated
          ref="company.billing.sepaValidAsOf"
          v-model="company.billing.sepaValidAsOf"
          input-class="w-full"
          :label="$t('modals.company.billing.sepaValidAsOf')"
        />
      </div>
    </div>
    <h2 class="flex items-center justify-between w-full my-4 font-medium uppercase border-b-4">
      {{ $t('modals.company.billing.representative') }}
    </h2>
    <div class="flex flex-wrap">
      <div class="w-full px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.companyName"
          v-model="company.billing.contact.companyName"
          :label="$t('modals.company.billing.companyName')"
          input-class="w-full"
          rules="maxLength:100"
        />
      </div>
      <div class="w-full px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.companyNameAddition"
          v-model="company.billing.contact.companyNameAddition"
          :label="$t('modals.company.billing.companyNameAddition')"
          input-class="w-full"
          rules="maxLength:50"
        />
      </div>
      <div class="w-1/2 px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.firstname"
          v-model="company.billing.contact.firstname"
          :label="$t('modals.company.billing.firstname')"
          input-class="w-full"
        />
      </div>
      <div class="w-1/2 px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.lastname"
          v-model="company.billing.contact.lastname"
          :label="$t('modals.company.billing.lastname')"
          input-class="w-full"
        />
      </div>
      <div class="w-1/3 px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.phone"
          v-model="company.billing.contact.phone"
          :label="$t('modals.company.billing.phone')"
          input-class="w-full"
          rules="phone"
        />
      </div>
      <div class="w-2/3 px-3 mb-3">
        <input-text-validated
          ref="company.billing.contact.email"
          v-model="company.billing.contact.email"
          :label="$t('modals.company.billing.email')"
          input-class="w-full"
          rules="email"
        />
      </div>
    </div>
    <h2 class="flex items-center w-full my-4 font-medium uppercase border-b-4">
      <input
        v-model="company.billing.hasSeparateBillingAddress"
        type="checkbox"
        class="w-4 h-4 mx-4 border-2 border-gray-600 form-checkbox"
        :value="company.billing.hasSeparateBillingAddress || false"
      >
      {{ $t('modals.company.billing.separateBillingAddress') }}
    </h2>
    <div class="flex flex-wrap">
      <div class="w-full px-3 mb-3">
        <input-text-validated
          ref="company.billing.address.street"
          v-model="company.billing.address.street"
          :label="$t('modals.company.billing.street')"
          input-class="w-full"
          :disabled="!company.billing.hasSeparateBillingAddress"
          :rules="addressRules"
        />
      </div>
      <div class="w-1/3 px-3 mb-3">
        <input-text-validated
          ref="company.billing.address.zip"
          v-model="company.billing.address.zip"
          :label="$t('modals.company.billing.zip')"
          input-class="w-full"
          :disabled="!company.billing.hasSeparateBillingAddress"
          :rules="addressRules"
        />
      </div>
      <div class="w-2/3 px-3 mb-3">
        <input-text-validated
          ref="company.billing.address.city"
          v-model="company.billing.address.city"
          :label="$t('modals.company.billing.city')"
          input-class="w-full"
          :disabled="!company.billing.hasSeparateBillingAddress"
          :rules="addressRules"
        />
      </div>
    </div>
  </div>
</template>

<script>
import InputTextValidated from '@/components/form/text-validated'
import InputSelectValidated from '@/components/form/select-validated'
import InputDateValidated from '@/components/form/date-validated'
import { getPaymentMethods } from '@/config'

export default {
  components: {
    InputTextValidated,
    InputSelectValidated,
    InputDateValidated
  },
  props: {
    company: { type: Object, default: () => ({}) }
  },
  computed: {
    paymentMethods () {
      // eslint-disable-next-line no-void
      void this.$i18n.locale
      return getPaymentMethods()
    },
    addressRules () {
      return this.company.billing.hasSeparateBillingAddress ? 'maxLength:50|required' : 'maxLength:50'
    },
    vatIdRules () {
      return this.company.billing.hasNoVatId
        ? 'maxLength:50'
        : 'required|maxLength:50'
    }
  },
  created () {
    if (typeof this.company.billing.hasNoVatId === 'undefined') {
      this.$set(this.company.billing, 'hasNoVatId', false)
    }

    if (this.company.billing.taxNumber && this.company.billing.taxNumber.trim() !== '') {
      this.company.billing.hasNoVatId = true
    }
  },

  methods: {
    toggleNoVatId () {
      this.$set(
        this.company.billing,
        'hasNoVatId',
        !this.company.billing.hasNoVatId
      )

      if (this.company.billing.hasNoVatId) {
        this.company.billing.vatId = ''
      } else {
        this.company.billing.taxNumber = ''
      }
    }
  }
}
</script>
